import "../styles/style.css";
import "../styles/chat-sidebar-item.css";
import emptyImage from "../images/empty.jpg";
import { trimWord, getTimeAgo, formatBoldText } from "../util/helperFunctions";
import React, { useState, useRef, useEffect } from "react";
import "font-awesome/css/font-awesome.min.css";
import ApiService from "../api/apiService";
import showSwal from "./swal";
import { common } from "../util/common";

function ChatSideBarItem(props) {
  const dropdownRef = useRef(null);
  const pinRef = useRef(null);
  const leaveRef = useRef(null);
  const [showDropdown, setShowDropdown] = useState(false);

  const { chatDetail, loading } = props;

  useEffect(() => {
    if (!showDropdown) return;

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [showDropdown]);

  if (loading) {
    return (
      <li className="inbox__item inbox__item--loading" aria-hidden="true">
        <div className="sidebar-item">
          <div className="skeleton skeleton-img inbox__item-img"></div>
          <div className="skeleton-item-details inbox__item-details">
            <span className="inbox__name">
              <div className="skeleton skeleton-text"></div>
            </span>
            <span className="inbox__message">
              <div className="skeleton skeleton-text"></div>
            </span>
          </div>
          <div className="skeleton skeleton-dot"></div>
        </div>
      </li>
    );
  }

  const { id, name, image, last_message, is_pinned, is_group, unread_count } = chatDetail;
  const { id: last_message_id, content, is_thread_reply, updatedAt, sender } = last_message;

  const senderName = sender.display_name;
  const senderChat = content;

  const handleItemClick = () => {
    const currentUrl = window.location.href;
    const conversationUrl = `/conversations/${id}`;

    if (is_thread_reply) {
      handleRedirection();
    } else {
      if (!currentUrl.includes(conversationUrl)) {
        window.location.href = conversationUrl;
      }
    }
  };

  const handlePin = async () => {
    const pinBtnId = pinRef.current.getAttribute("data-value");
    const _pinConversation = await ApiService.conversation.updateConversation({ action: "pin" }, pinBtnId);
    const { success } = _pinConversation;
    if (!success) showSwal(common.errors.PIN_CONVERSATION_ERROR);
  };

  const handleLeave = async () => {
    const leaveBtnId = leaveRef.current.getAttribute("data-value");
    const _leaveConversation = await ApiService.conversation.updateConversation({ action: "leave" }, leaveBtnId);
    const { success } = _leaveConversation;
    if (!success) showSwal(common.errors.LEAVE_CONVERSATION_ERROR);

    window.location.reload();
  };

  const resourcesUrl = is_group
    ? process.env.REACT_APP_IMAGES_DATA_RESOURCES_URL
    : process.env.REACT_APP_IMAGES_DATA_RESOURCES_URL;
  const FINAL_RESOURCES_URL = `${resourcesUrl}/${image}`;

  const conversationImage =
    image && image.includes("empty-filler-image.jpg")
      ? emptyImage
      : image !== "" && image !== null
      ? FINAL_RESOURCES_URL
      : emptyImage;
  const timeStamp = getTimeAgo(updatedAt);

  let finalSenderName = "",
    finalSenderChat = "",
    finalChatContent = "";
  if (senderName && senderChat) {
    finalSenderName = senderName.split(" ")[0];
    finalSenderChat = trimWord(senderChat, 25);
    finalChatContent = `${finalSenderName}: ${finalSenderChat}`;
  }

  const handleRedirection = async () => {
    const msgIndex = await ApiService.message.getMessagePosition(id, last_message_id);
    const { success, response } = msgIndex;
    if (!success) return showSwal(common.errors.GLOBAL_SEARCH_RESULT_CLICK_ERROR);

    const { thread, index, query } = response;
    const { thread_reply, message_id } = thread;
    if (thread_reply && unread_count > 0) {
      window.location.href = `/conversations/${id}?q=${last_message_id}&i=${index}&_q=${query}&_t=${message_id}&_tr=true`;
    } else {
      window.location.href = `/conversations/${id}`;
    }
  };

  const selectedConversationId = localStorage.getItem("selectedConversationId");

  let isSelectedConversation = false;
  if (selectedConversationId === id) {
    isSelectedConversation = true;
  }

  return (
    <li
      className={`inbox__item ${unread_count > 0 ? "inbox__item--unread" : ""} ${isSelectedConversation ? "chat-sidebar-selected" : ""} ${showDropdown ? "inbox__item--menu-open" : ""} ${is_pinned ? "inbox__item--pinned" : ""}`}
      data-value={id}
      data-name={name}
      data-sid={id}
    >

      <button
        type="button"
        className="sidebar-item sidebar-item__action"
        onClick={handleItemClick}
        aria-label={`Open conversation with ${name}`}
      >
        <input type="hidden" className="js-input-sellerId" value={id} />
        <img src={conversationImage} alt="Conversation avatar" className="inbox__item-img" />
        <div className="inbox__item-details">
          <span className="inbox__name-row">
            <span className="inbox__name">
              <b>{name}</b>
            </span>
            {is_pinned && (
              <span className="inbox__pinned-badge" aria-label="Pinned">
                PINNED
              </span>
            )}
          </span>
          <span className="inbox__message">
            {formatBoldText(finalChatContent)}
          </span>
        </div>
      </button>

      <div className="inbox__item-details inbox__item-details--2">
        <span className="inbox__message-time" aria-label={`Last updated ${timeStamp}`}>
          {timeStamp}
        </span>

        <div className="dropdown dropdown--hover" ref={dropdownRef}>
          <button
            className={`dropdown-btn ${showDropdown ? "dropdown-btn--open" : ""}`}
            onClick={(event) => {
              event.stopPropagation();
              setShowDropdown(!showDropdown);
            }}
            aria-haspopup="menu"
            aria-expanded={showDropdown}
            aria-label={`Conversation actions for ${name}`}
          >
            <i className="fa fa-ellipsis-v"></i>
          </button>

          <div className={`dropdown-content ${showDropdown ? "show" : ""}`} role="menu">
            <button
              data-value={id}
              onClick={(event) => {
                event.stopPropagation();
                handlePin();
                setShowDropdown(false);
              }}
              ref={pinRef}
              role="menuitem"
            >
              {is_pinned ? "Unpin" : "Pin"}
            </button>

            <button
              data-value={id}
              onClick={(event) => {
                event.stopPropagation();
                handleLeave();
                setShowDropdown(false);
              }}
              ref={leaveRef}
              role="menuitem"
            >
              Leave
            </button>
          </div>
        </div>

        {unread_count > 0 ? <span className="inbox__unread-count">{unread_count}</span> : null}
      </div>
    </li>
  );
}

export default ChatSideBarItem;
